/* PWA registration + install prompt + notification helpers.
 *
 * Imported once from src/index.js. Safe to call multiple times (idempotent).
 * All side-effects are wrapped in try/catch so an SW failure never breaks the
 * portal — the existing app continues to work without the SW.
 */

let deferredInstallPrompt = null;
let registered = false;
let updateAvailable = false;

export const isAppUpdateAvailable = () => updateAvailable;

export function registerServiceWorker() {
  if (typeof window === "undefined") return;
  if (!("serviceWorker" in navigator)) return;
  if (registered) return;
  // Don't try to install the SW during webpack-dev-server hot reload — only
  // register on built bundles or static-served pages. The signal we use is
  // simple: dev server injects a `webpack` global. Production builds don't.
  const isDev = process.env.NODE_ENV !== "production";
  // Allow SW even in dev so we can test in the preview environment; but
  // skip when running on localhost without HTTPS to avoid noisy errors.
  if (isDev && window.location.hostname === "localhost") return;
  registered = true;
  // A first installation needs no reload. A later update must never discard
  // an in-flight sign-in, attendance selfie or unsaved HR form.
  const wasControlledOnLoad = !!navigator.serviceWorker.controller;
  const notifyUpdate = () => {
    if (!wasControlledOnLoad || updateAvailable) return;
    updateAvailable = true;
    window.dispatchEvent(new CustomEvent('pwa-update-ready'));
  };

  const start = () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((reg) => {
        // Listen for updates and auto-activate the new worker.
        reg.addEventListener("updatefound", () => {
          const newSW = reg.installing;
          if (!newSW) return;
          newSW.addEventListener("statechange", () => {
            if (newSW.state === "installed" && navigator.serviceWorker.controller) {
              newSW.postMessage("SKIP_WAITING");
            }
          });
        });
      })
      .catch((err) => {
        // Non-fatal; portal still works without SW.
        // eslint-disable-next-line no-console
        console.warn("[PWA] Service worker registration failed:", err);
      });

    navigator.serviceWorker.addEventListener("controllerchange", notifyUpdate);
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data?.type === 'SW_UPDATED') notifyUpdate();
    });
  };
  if (document.readyState === 'complete') start();
  else window.addEventListener('load', start, { once: true });

  // Capture install prompt for later use (custom UI trigger).
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    window.dispatchEvent(new CustomEvent("pwa-installable"));
  });

  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    window.dispatchEvent(new CustomEvent("pwa-installed"));
  });
}

/** Returns true if the install prompt has been captured and is callable. */
export function isInstallable() {
  return !!deferredInstallPrompt;
}

/** Triggers the native install dialog. Resolves to 'accepted' | 'dismissed' | 'unavailable'. */
export async function promptInstall() {
  if (!deferredInstallPrompt) return "unavailable";
  try {
    deferredInstallPrompt.prompt();
    const choice = await deferredInstallPrompt.userChoice;
    deferredInstallPrompt = null;
    return choice && choice.outcome ? choice.outcome : "dismissed";
  } catch {
    return "dismissed";
  }
}

/** True when the app is running in installed (standalone) mode. */
export function isStandaloneInstalled() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia &&
    (window.matchMedia("(display-mode: standalone)").matches ||
      // iOS legacy
      window.navigator.standalone === true)
  );
}

/* ──────────── Notification helpers ──────────── */

/** Returns the current Notification permission ('granted'|'denied'|'default'|'unsupported'). */
export function notificationStatus() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission;
}

/** Asks the browser for notification permission. */
export async function requestNotificationPermission() {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  if (Notification.permission === "granted") return "granted";
  if (Notification.permission === "denied") return "denied";
  try {
    return await Notification.requestPermission();
  } catch {
    return "denied";
  }
}

/** Shows a local notification (via the active SW when available so it
 * persists like a real push, else falls back to a foreground Notification). */
export async function showLocalNotification(title, options = {}) {
  if (notificationStatus() !== "granted") return false;
  const opts = {
    icon: "/icons/icon-192.png",
    badge: "/icons/icon-192.png",
    ...options,
  };
  try {
    if ("serviceWorker" in navigator) {
      const reg = await navigator.serviceWorker.ready;
      await reg.showNotification(title, opts);
      return true;
    }
    // eslint-disable-next-line no-new
    new Notification(title, opts);
    return true;
  } catch {
    return false;
  }
}
